import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useRefs, today, statusColor } from "./OmsCommon";
import { MessageSquare, TrendingUp, ShoppingCart, AlertTriangle, Megaphone, Trophy } from "lucide-react";

const rp = (n) => "Rp" + Math.round(n || 0).toLocaleString("id-ID");

export default function OmsDashboard() {
  const refs = useRefs();
  const [f, setF] = useState({ date: today() });
  const [d, setD] = useState(null);
  const [hp, setHp] = useState("today");
  const [health, setHealth] = useState([]);
  const loadHealth = useCallback(async () => {
    const p = new URLSearchParams(Object.entries({ period: hp, store_id: f.store_id, marketplace_id: f.marketplace_id, admin: f.admin }).filter(([, v]) => v)).toString();
    try { setHealth((await api.get(`/oms/store-health?${p}`)).data.rows || []); } catch (e) { /* noop */ }
  }, [hp, f.store_id, f.marketplace_id, f.admin]);
  useEffect(() => { loadHealth(); }, [loadHealth]);

  const load = useCallback(async () => {
    const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
    try { setD((await api.get(`/oms/dashboard?${p}`)).data); } catch (e) { toast.error(apiError(e)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Online Shop Dashboard" subtitle="Monitoring harian seluruh toko online (10 toko).">
        <div className="flex flex-wrap gap-2">
          <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-40" data-testid="dash-date" />
          <select value={f.store_id || ""} onChange={(e) => setF({ ...f, store_id: e.target.value })} className="rounded border px-2 text-sm"><option value="">Semua Toko</option>{refs.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <select value={f.marketplace_id || ""} onChange={(e) => setF({ ...f, marketplace_id: e.target.value })} className="rounded border px-2 text-sm"><option value="">Semua MP</option>{refs.marketplaces.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
        </div>
      </PageHeader>

      {!d ? <p className="text-slate-400">Memuat...</p> : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard label="Skor Rata-rata Hari Ini" value={d.avg_score} icon={TrendingUp} accent="indigo" testId="dash-avg-score" />
            <StatCard label="Chat Response Rate" value={`${d.chat.response_rate}%`} icon={MessageSquare} accent="sky" />
            <StatCard label="Unanswered Chat" value={d.chat.unanswered} icon={AlertTriangle} accent="rose" />
            <StatCard label="Chat → Order" value={d.chat.converted_orders} icon={ShoppingCart} accent="emerald" />
            <StatCard label="Orders (ERP)" value={d.orders} icon={ShoppingCart} accent="sky" />
            <StatCard label="Revenue (ERP)" value={rp(d.revenue)} icon={TrendingUp} accent="emerald" />
            <StatCard label="Complaints" value={d.chat.complaints} icon={AlertTriangle} accent="amber" />
            <StatCard label="Biaya Iklan (ERP)" value={rp(d.advertising_cost)} icon={Megaphone} accent="indigo" />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="p-4">
              <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Trophy className="h-4 w-4 text-amber-500" /> Top Store Hari Ini</p>
              {d.top_store ? <div data-testid="dash-top-store"><p className="text-lg font-bold">{d.top_store.store_name}</p><p className={statusColor(d.top_store.status_label)}>{d.top_store.total_score} · {d.top_store.status_label}</p></div> : <p className="text-slate-400">Belum ada assessment.</p>}
            </Card>
            <Card className="p-4 lg:col-span-2">
              <p className="mb-2 text-sm font-semibold text-rose-600">Toko Perlu Perhatian ({d.needs_attention.length})</p>
              {d.needs_attention.length === 0 ? <p className="text-slate-400">Tidak ada.</p> : (
                <div className="flex flex-wrap gap-2" data-testid="dash-needs-attention">
                  {d.needs_attention.map((s) => <span key={s.store_id} className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">{s.store_name}: {s.total_score}</span>)}
                </div>
              )}
            </Card>
          </div>

          <Card className="mt-4 p-4">
            <p className="mb-2 text-sm font-semibold text-slate-700">Skor Seluruh Toko Hari Ini</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs text-slate-400"><th className="p-2">Toko</th><th className="text-right">Skor</th><th>Penilaian</th><th>Status</th></tr></thead>
                <tbody>{d.stores_today.length === 0 ? <tr><td colSpan="4" className="p-4 text-center text-slate-400">Belum ada assessment hari ini.</td></tr> : d.stores_today.map((s) => (
                  <tr key={s.store_id} className="border-b"><td className="p-2">{s.store_name}</td><td className="text-right font-semibold">{s.total_score}</td><td className={statusColor(s.status_label)}>{s.status_label}</td><td>{s.status}</td></tr>
                ))}</tbody></table>
            </div>
          </Card>

          <Card className="mt-4 p-4" data-testid="store-health-section">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-700">Kesehatan Toko</p>
              <div className="flex gap-1">
                {[["today", "Hari Ini"], ["7d", "7 Hari"], ["30d", "30 Hari"]].map(([v, l]) => (
                  <button key={v} onClick={() => setHp(v)} data-testid={`health-period-${v}`} className={`rounded px-3 py-1 text-xs font-medium ${hp === v ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs text-slate-400"><th className="p-2">Toko</th><th>Marketplace</th><th className="text-right">Rating</th><th className="text-right">Chat %</th><th className="text-right">Followers</th><th className="text-right">Orders</th><th className="text-right">Revenue</th><th className="text-right">Komplain</th><th className="text-right">Cancel</th><th className="text-right">Stockout</th><th>Status</th></tr></thead>
                <tbody>{health.length === 0 ? <tr><td colSpan="11" className="p-4 text-center text-slate-400" data-testid="health-empty">Belum ada snapshot toko untuk periode ini.</td></tr> : health.map((h, i) => (
                  <tr key={i} data-testid={`health-row-${i}`} className="border-b"><td className="p-2">{h.store_name}</td><td>{h.marketplace_name}</td>
                    <td className="text-right">{h.rating == null ? "—" : h.rating}</td><td className="text-right">{h.chat_performance == null ? "—" : h.chat_performance}</td>
                    <td className="text-right">{h.followers == null ? "—" : h.followers}</td><td className="text-right">{h.orders}</td><td className="text-right">{rp(h.revenue)}</td>
                    <td className="text-right">{h.complaints}</td><td className="text-right">{h.cancellation}</td><td className="text-right">{h.stockout == null ? "—" : h.stockout}</td>
                    <td className={statusColor(h.store_status)}>{h.store_status || "—"}</td></tr>
                ))}</tbody></table>
            </div>
          </Card>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[["Tren 7 Hari", d.trend_7d], ["Tren 30 Hari", d.trend_30d]].map(([title, tr]) => (
              <Card key={title} className="p-4">
                <p className="mb-2 text-sm font-semibold text-slate-700">{title}</p>
                <div className="flex h-32 items-end gap-0.5" data-testid={`trend-${tr.length}`}>
                  {tr.map((t) => (
                    <div key={t.date} title={`${t.date}: skor ${t.avg_score}, resp ${t.response_rate}%`} className="flex-1 rounded-t bg-indigo-500/70" style={{ height: `${Math.max(4, t.avg_score)}%` }} />
                  ))}
                </div>
                <p className="mt-1 text-[10px] text-slate-400">Tinggi bar = skor rata-rata harian.</p>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
